import * as THREE from 'three';

type Cell = {count: number;nx: number;ny: number;nz: number;};
type Component = {keys: number[];score: number;centroid: THREE.Vector3;radius: number;dilated: Set<number>;};

const G = 40;
const keyOf = (x: number, y: number, z: number) => x + y * G + z * G * G;
const decode = (k: number): [number, number, number] => [k % G, Math.floor(k / G) % G, Math.floor(k / (G * G))];

/**
 * Simulated "AI" feature finder for gears/cogs. Gear teeth pack many vertices with rapidly
 * changing normals into a small volume, so we voxelise the model, score each cell by
 * vertex density × normal variation, cluster the hottest cells and return the two most
 * compact clusters as per-mesh vertex index lists.
 */
export function detectGearRegions(meshes: THREE.Mesh[], maxRegions = 2): {indices: number[][];regions: number;} {
  const box = new THREE.Box3();
  meshes.forEach((m) => box.expandByObject(m));
  const size = box.getSize(new THREE.Vector3());
  const cell = Math.max(size.x, size.y, size.z) / 18 || 1;
  const cellOf = (v: THREE.Vector3) =>
  keyOf(
    Math.min(G - 1, Math.floor((v.x - box.min.x) / cell)),
    Math.min(G - 1, Math.floor((v.y - box.min.y) / cell)),
    Math.min(G - 1, Math.floor((v.z - box.min.z) / cell))
  );
  const centerOf = (k: number) => {
    const [x, y, z] = decode(k);
    return new THREE.Vector3(box.min.x + (x + 0.5) * cell, box.min.y + (y + 0.5) * cell, box.min.z + (z + 0.5) * cell);
  };

  const total = meshes.reduce((s, m) => s + m.geometry.attributes.position.count, 0);
  const stride = Math.max(1, Math.ceil(total / 150000));
  const cells = new Map<number, Cell>();
  const v = new THREE.Vector3();
  const n = new THREE.Vector3();
  const nm = new THREE.Matrix3();

  meshes.forEach((mesh) => {
    const pos = mesh.geometry.attributes.position;
    const nor = mesh.geometry.attributes.normal;
    nm.getNormalMatrix(mesh.matrixWorld);
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      const k = cellOf(v);
      const c = cells.get(k) ?? { count: 0, nx: 0, ny: 0, nz: 0 };
      c.count++;
      if (nor) {
        n.fromBufferAttribute(nor, i).applyMatrix3(nm).normalize();
        c.nx += n.x;
        c.ny += n.y;
        c.nz += n.z;
      }
      cells.set(k, c);
    }
  });

  const scored = [...cells.entries()].
  filter(([, c]) => c.count >= 6).
  map(([k, c]) => ({ k, s: c.count * (1 - Math.hypot(c.nx, c.ny, c.nz) / c.count) }));
  if (scored.length === 0) return { indices: meshes.map(() => []), regions: 0 };
  const sorted = scored.map((x) => x.s).sort((a, b) => a - b);
  const threshold = sorted[Math.floor(sorted.length * 0.88)];
  const hot = new Map(scored.filter((x) => x.s >= threshold).map((x) => [x.k, x.s]));

  // Connected components over the 26-neighbourhood
  const seen = new Set<number>();
  const comps: Component[] = [];
  hot.forEach((_, start) => {
    if (seen.has(start)) return;
    const keys: number[] = [];
    const queue = [start];
    seen.add(start);
    while (queue.length) {
      const k = queue.pop() as number;
      keys.push(k);
      const [x, y, z] = decode(k);
      for (let dx = -1; dx <= 1; dx++)
      for (let dy = -1; dy <= 1; dy++)
      for (let dz = -1; dz <= 1; dz++) {
        const nk = keyOf(x + dx, y + dy, z + dz);
        if (!seen.has(nk) && hot.has(nk)) {
          seen.add(nk);
          queue.push(nk);
        }
      }
    }
    let score = 0;
    const centroid = new THREE.Vector3();
    keys.forEach((k) => {
      const s = hot.get(k) ?? 0;
      score += s;
      centroid.addScaledVector(centerOf(k), s);
    });
    centroid.divideScalar(score || 1);
    const radius = Math.max(...keys.map((k) => centerOf(k).distanceTo(centroid))) + cell * 0.8;
    const dilated = new Set<number>();
    keys.forEach((k) => {
      const [x, y, z] = decode(k);
      for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) for (let dz = -1; dz <= 1; dz++) dilated.add(keyOf(x + dx, y + dy, z + dz));
    });
    comps.push({ keys, score, centroid, radius, dilated });
  });

  const picked = comps.
  filter((c) => c.keys.length >= 2).
  sort((a, b) => b.score / Math.sqrt(b.keys.length) - a.score / Math.sqrt(a.keys.length)).
  slice(0, maxRegions);

  const indices = meshes.map((mesh) => {
    const out: number[] = [];
    const pos = mesh.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      const k = cellOf(v);
      if (picked.some((c) => c.dilated.has(k) && v.distanceTo(c.centroid) <= c.radius)) out.push(i);
    }
    return out;
  });

  return { indices, regions: picked.length };
}
